<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Analysis Form Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <analysis-card
      v-if="analysis"
      :analysis="analysis"
    />
    <b-form @submit.prevent="onSubmit">
      <b-row class="mt-3">
        <b-col>
          <b-card no-body>
            <b-card-header>
              <span class="text-muted text-uppercase"
                >Analysis Identification</span
              >
            </b-card-header>
            <div class="py-3 px-5">
              <b-form-group label="Sample Type" :label-cols="2">
                <multiselect
                  v-model="selected_sample_type"
                  :options="sample_type_options"
                  :multiple="false"
                  track-by="id"
                  label="name"
                />
              </b-form-group>
              <b-form-group label="Analyte" :label-cols="2">
                <multiselect
                  v-model="selected_analyte"
                  :disabled="!sample_type_id"
                  :options="analyte_options"
                  :multiple="false"
                  track-by="id"
                  label="name"
                />
              </b-form-group>
              <b-form-group label="Name" :label-cols="2">
                <b-form-input
                  v-model="form_data.name"
                  type="text"
                  placeholder="Name"
                />
              </b-form-group>
              <b-form-group label="MODUS Name" :label-cols="2">
                <b-form-input
                  v-model="form_data.modus_name"
                  type="text"
                  placeholder="MODUS Name"
                />
              </b-form-group>
              <b-form-group label="Extraction Method" :label-cols="2">
                <b-form-input
                  v-model="form_data.extraction_method"
                  type="text"
                  placeholder="Extraction Method"
                />
              </b-form-group>
              <b-form-group label="Measurement Method" :label-cols="2">
                <b-form-input
                  v-model="form_data.measurement_method"
                  type="text"
                  placeholder="Measurement Method"
                />
              </b-form-group>
              <b-form-group label="Description" :label-cols="2">
                <b-form-textarea
                  v-model="form_data.description"
                  placeholder="Description"
                />
              </b-form-group>
            </div>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col>
          <b-card no-body>
            <b-card-header>
              <span class="text-muted text-uppercase">Thresholds</span>
            </b-card-header>
            <div class="py-3 px-5">
              <b-form-group label="Unit of Measure" :label-cols="2">
                <multiselect
                  v-model="selected_threshold_unit"
                  :options="threshold_unit_options"
                  :multiple="false"
                  track-by="id"
                  label="name"
                  :custom-label="unitLabel"
                />
              </b-form-group>
              <b-form-group label="Significant Figures" :label-cols="2">
                <b-form-input
                  v-model="form_data.significant_figures"
                  type="number"
                  placeholder="Significant Figures"
                />
              </b-form-group>
              <b-form-group label="Threshold Detection Limit" :label-cols="2">
                <b-form-input
                  v-model="form_data.threshold_detection_limit"
                  type="number"
                  placeholder="Threshold detection limit"
                />
              </b-form-group>
              <b-form-group label="Threshold Max Value" :label-cols="2">
                <b-form-input
                  v-model.number="form_data.threshold_max"
                  :state="!$v.form_data.threshold_max.$invalid && thresholdMax"
                  aria-describedby="threshold_max-feedback"
                  invalid-feedback="Required"
                  type="number"
                  placeholder="Threshold max value"
                />
                <b-form-invalid-feedback id="threshold_max-feedback">
                  Threshold Max Value must be greater than Threshold Min Value
                  and greater then or equal to Threshold Max Warning Value
                </b-form-invalid-feedback>
              </b-form-group>
              <b-form-group label="Threshold Max Warning" :label-cols="2">
                <b-form-input
                  v-model.number="form_data.threshold_max_warning"
                  :state="
                    !$v.form_data.threshold_max_warning.$invalid &&
                      thresholdMaxWarning
                  "
                  type="number"
                  aria-describedby="threshold_max_warning-feedback"
                  invalid-feedback="Required"
                  placeholder="Threshold max warning"
                />
                <b-form-invalid-feedback id="threshold_max_warning-feedback">
                  Threshold Max Warning Value must be less than Threshold Max
                  Value
                </b-form-invalid-feedback>
              </b-form-group>
              <b-form-group label="Threshold Min Value" :label-cols="2">
                <b-form-input
                  v-model.number="form_data.threshold_min"
                  :state="!$v.form_data.threshold_min.$invalid && thresholdMin"
                  type="number"
                  aria-describedby="threshold_min-feedback"
                  placeholder="Threshold min value"
                />
                <b-form-invalid-feedback id="threshold_min-feedback">
                  Threshold Min Value must be less than Threshold Max Value and
                  less then or equal to Threshold Min Warning
                </b-form-invalid-feedback>
              </b-form-group>
              <b-form-group label="Threshold Min Warning" :label-cols="2">
                <b-form-input
                  v-model.number="form_data.threshold_min_warning"
                  :state="
                    !$v.form_data.threshold_min_warning.$invalid &&
                      thresholdMinWarning
                  "
                  type="number"
                  aria-describedby="threshold_min_warning-feedback"
                  placeholder="Threshold min warning"
                />
                <b-form-invalid-feedback id="threshold_min_warning-feedback">
                  Threshold Min Warning Value must be greater than Threshold Min
                  Value
                </b-form-invalid-feedback>
              </b-form-group>
            </div>
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-button
            @click="submitForm"
            class="my-4 float-right py-3 px-5"
            variant="success"
            ><i class="fal fa-address-book" /> Save</b-button
          >
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>
<script>
import Multiselect from "vue-multiselect";
import AnalysisCard from "@/components/lab/analysis/AnalysisCard";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import { getCollection } from "@/shared/fetch-utils";
import { validationMixin } from "vuelidate/src";
import { required } from "vuelidate/lib/validators";

export default {
  name: "AnalysisForm",
  mixins: [validationMixin, ToastNotifications],
  components: {
    AnalysisCard,
    Multiselect
  },
  props: {
    analysis: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      loading: true,
      form_data: {
        analyte_id: null,
        description: null,
        extraction_method: null,
        measurement_method: null,
        modus_name: null,
        name: null,
        significant_figures: null,
        threshold_detection_limit: null,
        threshold_max: null,
        threshold_max_warning: null,
        threshold_min: null,
        threshold_min_warning: null,
        threshold_unit_id: null,
      },
      analyte_collection: null,
      analyte_options: [],
      analyte_value: null,
      sample_type_collection: null,
      sample_type_id: null,
      sample_type_options: [],
      sample_type_value: null,
      threshold_unit_collection: null,
      threshold_unit_options: [],
      threshold_unit_value: null,
    };
  },
  computed: {
    selected_sample_type: {
      get() {
        return this.sample_type_value;
      },
      set(value) {
        this.sample_type_id = value && value.id;
        this.sample_type_value = value;
        this.form_data.analyte_id = null;
        this.analyte_value = null;
        this.fetchAnalytes();
      }
    },
    selected_analyte: {
      get() {
        return this.analyte_value;
      },
      set(value) {
        this.form_data.analyte_id = value && value.id;
        this.analyte_value = value;
      }
    },
    selected_threshold_unit: {
      get() {
        return this.threshold_unit_value;
      },
      set(value) {
        this.form_data.threshold_unit_id = value && value.id;
        this.threshold_unit_value = value;
      }
    },
    thresholdMax() {
      return (
        this.form_data.threshold_max > this.form_data.threshold_min &&
        this.form_data.threshold_max >= this.form_data.threshold_max_warning
      );
    },
    thresholdMin() {
      return (
        this.form_data.threshold_min < this.form_data.threshold_max &&
        this.form_data.threshold_min <= this.form_data.threshold_min_warning
      );
    },
    thresholdMaxWarning() {
      return (
        this.form_data.threshold_max_warning <= this.form_data.threshold_max
      );
    },
    thresholdMinWarning() {
      return (
        this.form_data.threshold_min_warning >= this.form_data.threshold_min
      );
    }
  },
  validations: {
    form_data: {
      threshold_max: { required },
      threshold_max_warning: { required },
      threshold_min: { required },
      threshold_min_warning: { required }
    }
  },
  methods: {
    unitLabel({ name, type }) {
      return type ? `${name} (${type})` : name;
    },
    getOptions(collection) {
      return collection.models.map(({ id, name }) => {
        return {
          id,
          name
        };
      });
    },
    async initFormData() {
      const { analyte, analyte_id, threshold_unit_id } = this.analysis;

      this.form_data = this.analysis.cloneAttributes(true, true, [ "id" ]);

      threshold_unit_id && (
        this.threshold_unit_value = this.threshold_unit_options.find(({ id }) => id === threshold_unit_id)
      )

      if (analyte_id) {
        const { sample_type_id } = analyte;

        if (sample_type_id) {
          this.sample_type_id = sample_type_id;
          this.sample_type_value = this.sample_type_options.find(({ id }) => id === sample_type_id)

          await this.fetchAnalytes();

          this.analyte_value = this.analyte_options.find(({ id }) => id === analyte_id);
          this.loading = false;
        }
      }

      this.loading = false;
    },
    async init() {
      this.sample_type_collection = await getCollection({
        collection_type: "sample_type",
        message: this.$bvToast
      });
      this.threshold_unit_collection = await getCollection({
        collection_type: "measure_unit",
        limit: "all",
        message: this.$bvToast
      });

      this.sample_type_options = this.getOptions(this.sample_type_collection);
      this.threshold_unit_options = this.threshold_unit_collection.models.map(({ id, name, type }) => ({ id, name, type }));
      this.analysis && this.initFormData();
      this.loading = false;
    },
    async fetchAnalytes() {
      if (this.sample_type_id) {
        this.analyte_collection = await getCollection({
          collection_type: "analyte",
          filters: [
            {
              logic: "filterEquals",
              field: "sample_type_id",
              value: this.sample_type_id
            }
          ],
          limit: "all",
          message: this.$bvToast
        });

        this.analyte_options = this.getOptions(this.analyte_collection);
      }
    },
    submitForm() {
      return this.$emit("submit", this.form_data);
    }
  },
  created() {
    this.loading = true;

    this.init();
  }
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
